import React, { useEffect, useState } from "react";
import { Grid, Collapse } from "@material-ui/core";
import {
  ButtonIcon,
  ICONS,
  SecondaryButton,
  PrimaryButton,
  ButtonSizeOptions
} from "@loanbossinc/component-library";

import { SupervisedAssumptionFormContent } from "./SupervisedAssumptionFormContent";

export const handleReset = (setFieldValue, savedAssumptions, loanTerm, propsCapRate, propsNoi, isFavorite, favoriteAssumption) => {
  if (!isFavorite) {
    const loanTypeId = savedAssumptions?.spread?.loanTypeSetting?.loanType?.value;
    const rateTypeId = savedAssumptions?.spread?.loanTypeSetting?.rateType?.value;
    const rateIndexId = savedAssumptions?.spread?.loanTypeSetting?.rateIndex?.value;
    const preferredConstraintId = savedAssumptions?.spread?.loanTypeSetting?.preferredConstraint?.value;
    const daycountId = savedAssumptions?.spread?.loanTypeSetting?.dayCount?.value;
    const amortTypeId = savedAssumptions?.spread?.loanTypeSetting?.amortType?.value;
    const spread = savedAssumptions?.spread?.spread;
    const ioPeriodsForCashflows = savedAssumptions?.spread?.loanTypeSetting?.ioPeriods;
    const amortRateIfFloating = savedAssumptions?.spread?.loanTypeSetting?.amortRateIfFloatingForCashflows;

    const {
      amortTermMonths,
      amortTermMonthsForCashflows,
      closingCostsPercentage,
      amortRateIfFloatingForCashflows,
      uwRate
    } = savedAssumptions?.spread?.loanTypeSetting || {};

    const {
      ltvMax,
      dscrMin,
      debtYieldMin
    } = savedAssumptions?.spread?.constraint || {};

    setFieldValue("lpsRateIndex", rateIndexId ?? "");
    setFieldValue("lpsAmortizationTerm", amortTermMonths ?? "");
    setFieldValue("lpsAmortizationRate", amortRateIfFloating ?? "");
    setFieldValue("lpsAmortizationType", amortTypeId ?? "");
    setFieldValue("lpsClosingCosts", closingCostsPercentage * 100 ?? "");
    setFieldValue("lpsRateType", rateTypeId ?? "");
    setFieldValue("lpsAmortizationTermForCashflows", amortTermMonthsForCashflows ?? "");
    setFieldValue("lpsAmortizationRateForCashflows", amortRateIfFloatingForCashflows ?? "");
    setFieldValue("lpsIoPeriods", ioPeriodsForCashflows ?? "");
    setFieldValue("lpsDaycount", daycountId ?? "");
    setFieldValue("lpsConstraintPreferenceId", preferredConstraintId ?? "");
    setFieldValue("loanTypeId", loanTypeId ?? "");
    setFieldValue("ltvMax", ltvMax * 100 ?? "");
    setFieldValue("dscrMin", dscrMin ?? "");
    setFieldValue("debtYieldMin", debtYieldMin * 100 ?? "");
    setFieldValue("spread", spread * 100 ?? "");
    setFieldValue("uwRate", uwRate * 100 ?? "");

    setFieldValue("futureCapRate", propsCapRate * 100 ?? "");
    setFieldValue("startDate", new Date() ?? "");
    setFieldValue("loanTerm", loanTerm * 12 ?? "");
    setFieldValue("noi", propsNoi ?? "");
  } else {
    const {
      UwRateRaw,
      amortization,
      amortizationRate,
      amortizationTerm,
      amortizationTypeId,
      closingCosts,
      dayCountId,
      indexId,
      interestOnlyPeriods,
      loanSpread,
      loanType,
      maxLTV,
      minDSCR,
      minDebtYield,
      preferredConstraintId,
      rateTypeId,
      term,
      noi,
      capRate
    } = favoriteAssumption;

    setFieldValue("lpsRateIndex", indexId ?? "");
    setFieldValue("lpsAmortizationTerm", amortization ?? "");
    setFieldValue("lpsAmortizationRate", amortizationRate ?? "");
    setFieldValue("lpsAmortizationType", amortizationTypeId ?? "");
    setFieldValue("lpsClosingCosts", closingCosts ?? "");
    setFieldValue("lpsRateType", rateTypeId ?? "");
    setFieldValue("lpsAmortizationTermForCashflows", amortizationTerm ?? "");
    setFieldValue("lpsAmortizationRateForCashflows", amortizationRate ?? "");
    setFieldValue("lpsIoPeriods", interestOnlyPeriods ?? "");
    setFieldValue("lpsDaycount", dayCountId ?? "");
    setFieldValue("lpsConstraintPreferenceId", preferredConstraintId ?? "");
    setFieldValue("loanTypeId", loanType ?? "");
    setFieldValue("ltvMax", maxLTV ?? "");
    setFieldValue("dscrMin", minDSCR ?? "");
    setFieldValue("debtYieldMin", minDebtYield ?? "");
    setFieldValue("spread", loanSpread ?? "");
    setFieldValue("noi", noi ?? "");
    setFieldValue("futureCapRate", capRate * 100 ?? "");
    setFieldValue("startDate", new Date() ?? "");
    setFieldValue("loanTerm", term ?? "");
    setFieldValue("uwRate", UwRateRaw * 100 ?? "");
  }
};

const SupervisedAssumptionCard = ({
  isLoading,
  isFavorite,
  favoriteAssumption,
  loanTerm,
  capRate,
  noi,
  onReset,
  subtitle,
  preferredConstraintsList,
  handleSubmit,
  savedAssumptions,
  ...formikProps
}) => {
  const [isCollapsed, setIsCollapsed] = useState(isLoading);

  useEffect(() => {
    setIsCollapsed(isLoading);
  }, [isLoading]);

  const overflow = !isCollapsed ? { overflow: "visible" } : { overflow: "hidden" };

  return (
    <div className="assumptions-card-wrapper">
      <Grid container direction="column" xs={12}>
        <Grid item xs={12}>
          <div className="assumptions-card-title-section-wrapper">
            <div className="assumptions-card-title-text">
              Assumptions
              <div className="supervised-assumptions-header-subtitle">{subtitle}</div>
            </div>
            <ButtonIcon
              disabled={isLoading}
              onClick={() => setIsCollapsed(!isCollapsed)}
              iconName={isCollapsed ? ICONS.UNFOLD_MORE : ICONS.UNFOLD_LESS}
            />
          </div>
        </Grid>
        <Collapse in={!isCollapsed} style={overflow}>
          <SupervisedAssumptionFormContent
            capRate={capRate}
            loanTermYears={loanTerm}
            isFavorite={isFavorite}
            favoriteAssumption={favoriteAssumption}
            noi={noi}
            preferredConstraintsList={preferredConstraintsList}
            savedAssumptions={savedAssumptions}
            {...formikProps}
          />
          <Grid item>
            <div className="assumptions-card-buttons-section-wrapper">
              <SecondaryButton
                className="assumptions-card-buttons"
                label="Reset"
                size={ButtonSizeOptions.smFluid}
                onClick={() => {
                  onReset();
                  handleReset(formikProps.setFieldValue, savedAssumptions, loanTerm, capRate, noi, isFavorite, favoriteAssumption);
                }}
              />
              <PrimaryButton
                className="assumptions-card-buttons"
                label={(!isFavorite) ? "Calculate" : "Save & Calculate"}
                size={ButtonSizeOptions.smFluid}
                onClick={handleSubmit}
              />
            </div>
          </Grid>
        </Collapse>
      </Grid>
    </div>
  );
};

export default SupervisedAssumptionCard;
